/**
 * Mapea un course_id cuando el usuario cambia de año en el selector.
 *
 * Reglas:
 * - Usa la misma lógica conceptual que el backend (transformCourseCode),
 *   pero soportando ir tanto hacia adelante (año+1) como hacia atrás (año-1).
 * - SCHOOL#zsc000001 con G000001 nunca cambia.
 *
 * IMPORTANTE: Esto es solo para navegación en frontend, no crea/borra cursos.
 */
export function mapCourseCodeForYearChange(courseCode, schoolId, fromYear, toYear) {
    const from = parseInt(fromYear, 10);
    const to = parseInt(toYear, 10);
    if (Number.isNaN(from) || Number.isNaN(to) || from === to) {
        return courseCode;
    }
    const delta = to - from;
    // Excepción: SCHOOL#zsc000001 con G000001 no cambia nunca
    if (schoolId === "zsc000001" && courseCode === "G000001") {
        return courseCode;
    }
    // Centro de Padres se mantiene igual
    if (courseCode === "CPA") {
        return courseCode;
    }
    // Hacia adelante (año siguiente): misma lógica que el backend
    if (delta === 1) {
        return forwardTransform(courseCode);
    }
    // Hacia atrás (año anterior): inversa de la lógica del backend
    if (delta === -1) {
        return backwardTransform(courseCode);
    }
    // Para saltos mayores a 1 año (no contemplados hoy) devolvemos el mismo código
    return courseCode;
}
function forwardTransform(courseCode) {
    // Pre-Kinder → Kinder
    if (courseCode === "PKA")
        return "KA";
    if (courseCode === "PKB")
        return "KB";
    // Kinder → 1° Básico
    if (courseCode === "KA")
        return "1BA";
    if (courseCode === "KB")
        return "1BB";
    // 4° Medio → se queda igual (para navegación no lo "promovemos")
    if (courseCode.startsWith("4M")) {
        return courseCode;
    }
    // Básico: 1BA → 2BA, ..., 8BA → 1MA
    const basicMatch = courseCode.match(/^([1-8])(B)([A-Z])$/);
    if (basicMatch) {
        const level = parseInt(basicMatch[1], 10);
        const section = basicMatch[3];
        if (level === 8) {
            // 8° Básico → 1° Medio
            return `1M${section}`;
        }
        else {
            return `${level + 1}B${section}`;
        }
    }
    // Medio: 1MA → 2MA, 2MA → 3MA, 3MA → 4MA
    const mediumMatch = courseCode.match(/^([1-3])(M)([A-Z])$/);
    if (mediumMatch) {
        const level = parseInt(mediumMatch[1], 10);
        const section = mediumMatch[3];
        if (level === 3) {
            return `4M${section}`;
        }
        else {
            return `${level + 1}M${section}`;
        }
    }
    return courseCode;
}
function backwardTransform(courseCode) {
    // Kinder ← Pre-Kinder (inverso)
    if (courseCode === "KA")
        return "PKA";
    if (courseCode === "KB")
        return "PKB";
    // 1° Básico ← Kinder
    if (courseCode === "1BA")
        return "KA";
    if (courseCode === "1BB")
        return "KB";
    // 1° Medio ← 8° Básico
    const mediumMatchFromFirst = courseCode.match(/^1M([A-Z])$/);
    if (mediumMatchFromFirst) {
        const section = mediumMatchFromFirst[1];
        return `8B${section}`;
    }
    // Básico: 2BA → 1BA, ..., 8BA → 7BA
    const basicMatch = courseCode.match(/^([1-8])(B)([A-Z])$/);
    if (basicMatch) {
        const level = parseInt(basicMatch[1], 10);
        const section = basicMatch[3];
        if (level === 1) {
            // 1BA/1BB ya fueron manejados arriba (Kinder), así que aquí devolvemos tal cual
            return courseCode;
        }
        return `${level - 1}B${section}`;
    }
    // Medio: 2MA → 1MA, 3MA → 2MA, 4MA → 3MA
    const mediumMatch = courseCode.match(/^([1-4])(M)([A-Z])$/);
    if (mediumMatch) {
        const level = parseInt(mediumMatch[1], 10);
        const section = mediumMatch[3];
        if (level === 1) {
            // 1M* ya manejado como caso especial arriba
            return courseCode;
        }
        return `${level - 1}M${section}`;
    }
    return courseCode;
}
